import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const MS_PER_STROKE = 1000;

interface UseGateForcePlaybackParams {
  strokeLineCount: number;
}

export const useGateForcePlayback = ({ strokeLineCount }: UseGateForcePlaybackParams) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const progressRef = useRef(progress);
  const playbackFrameRef = useRef<number | null>(null);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setPrefersReducedMotion(mq.matches);
    sync();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion && isPlaying) setIsPlaying(false);
  }, [prefersReducedMotion, isPlaying]);

  // Per-stroke animation: smooth drawing within each stroke, 1 s per stroke.
  useEffect(() => {
    if (!isPlaying || strokeLineCount === 0 || prefersReducedMotion) return undefined;

    const total = strokeLineCount;
    let currentIdx = Math.min(Math.floor((progressRef.current / 100) * total), total - 1);
    if (progressRef.current >= 100) currentIdx = 0;

    let frameId: number | null = null;
    let strokeStartTs: number | null = null;

    const animate = (timestamp: number) => {
      if (strokeStartTs === null) strokeStartTs = timestamp;

      const elapsed = timestamp - strokeStartTs;
      const duration = MS_PER_STROKE / speed;
      const t = Math.min(elapsed / duration, 1);

      const strokeStart = (currentIdx / total) * 100;
      const strokeEnd = ((currentIdx + 1) / total) * 100;
      setProgress(strokeStart + (strokeEnd - strokeStart) * t);

      if (t >= 1) {
        currentIdx += 1;
        if (currentIdx >= total) {
          setProgress(100);
          setIsPlaying(false);
          return;
        }
        strokeStartTs = timestamp;
      }

      frameId = window.requestAnimationFrame(animate);
      playbackFrameRef.current = frameId;
    };

    frameId = window.requestAnimationFrame(animate);
    playbackFrameRef.current = frameId;

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      if (playbackFrameRef.current === frameId) playbackFrameRef.current = null;
    };
  }, [isPlaying, speed, strokeLineCount, prefersReducedMotion]);

  useEffect(() => {
    return () => {
      if (playbackFrameRef.current !== null) {
        window.cancelAnimationFrame(playbackFrameRef.current);
        playbackFrameRef.current = null;
      }
    };
  }, []);

  // Track progress: snaps to stroke centers for the progress bar dots.
  // Subtract epsilon before floor to handle exact boundary values correctly
  // (e.g., progress at stroke end should map to that stroke, not the next).
  const trackProgress = useMemo(() => {
    if (strokeLineCount === 0) return progress;
    if (progress >= 100) return 100;
    if (progress <= 0) return 0;
    const idx = Math.min(
      Math.max(Math.floor((progress / 100) * strokeLineCount - 1e-9), 0),
      strokeLineCount - 1
    );
    return ((idx + 0.5) / strokeLineCount) * 100;
  }, [progress, strokeLineCount]);

  const handlePlayPause = useCallback(() => {
    setHasInteracted(true);
    if (prefersReducedMotion) {
      setIsPlaying(false);
      setProgress((prev) => (prev >= 100 ? 0 : 100));
      return;
    }
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }
    if (progressRef.current >= 100) setProgress(0);
    setIsPlaying(true);
  }, [isPlaying, prefersReducedMotion]);

  const handleReset = useCallback(() => {
    setIsPlaying(false);
    setProgress(0);
    setHasInteracted(false);
  }, []);

  const handleSpeedChange = useCallback((nextSpeed: number) => {
    setSpeed(nextSpeed);
  }, []);

  const handleProgressChange = useCallback((nextProgress: number) => {
    setHasInteracted(true);
    setIsPlaying(false);
    setProgress(Math.min(Math.max(nextProgress, 0), 100));
  }, []);

  const handleScrubDirection = useCallback(
    (direction: 1 | -1) => {
      if (strokeLineCount === 0) return;
      setHasInteracted(true);
      setIsPlaying(false);
      setProgress((prev) => {
        const total = strokeLineCount;
        const currentIdx = Math.min(
          Math.max(Math.floor((prev / 100) * total - 1e-9), 0),
          total - 1
        );
        const nextIdx = Math.min(Math.max(currentIdx + direction, 0), total - 1);
        return ((nextIdx + 1) / total) * 100;
      });
    },
    [strokeLineCount]
  );

  return {
    isPlaying,
    progress,
    trackProgress,
    speed,
    hasInteracted,
    prefersReducedMotion,
    handlePlayPause,
    handleReset,
    handleSpeedChange,
    handleProgressChange,
    handleScrubDirection,
  };
};
